import { useLocale } from '@insio/ui-toolkit';
import { formatBillingPeriod, formatShortDate, plainDateFrom } from '@insio/dates';

/** Period label formats the backend emits as raw buckets. */
export type PeriodLabelFormat = 'month' | 'quarter' | 'year' | 'date';

const KNOWN_FORMATS: readonly PeriodLabelFormat[] = ['month', 'quarter', 'year', 'date'];

export function isPeriodLabelFormat(value: unknown): value is PeriodLabelFormat {
  return typeof value === 'string' && (KNOWN_FORMATS as readonly string[]).includes(value);
}

/**
 * Returns a formatter turning a raw period bucket into a locale-aware label:
 * "2012-01" → "led 2012", "2012-01-15" → "15. 1. 2012". Unparseable input stays as-is.
 */
export function useFormatPeriodLabel(): (label: string, format?: PeriodLabelFormat) => string {
  const locale = useLocale();

  return (label, format) => {
    try {
      switch (format) {
        case 'month':
          return formatBillingPeriod(label, locale);
        case 'date':
          return formatShortDate(plainDateFrom(label.slice(0, 10)), locale);
        // Quarter ("2012-Q1") and year ("2012") read the same in every locale.
        default:
          return label;
      }
    } catch {
      return label;
    }
  };
}
